import { HttpClient, HttpHeaders, HttpEvent } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { tap, catchError, finalize } from 'rxjs/operators';
import { baseUrl } from '../../../environments/environment';
import { SecurityService } from './security.service';
import { ToastrService } from 'ngx-toastr';

export type LoaderType = 'content' | 'full';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  /** Content/table shimmer — does not cover sidebar/layout */
  isLoading: boolean = false;
  /** Full blank-screen theme loader — login/logout only */
  isFullPageLoading: boolean = false;

  private contentLoadingCount = 0;
  private fullPageLoadingCount = 0;

  constructor(private http: HttpClient, private securityService: SecurityService, public router: Router, public toaster: ToastrService) { }
  myEvent = new EventEmitter

  post(url: string, data: any, isAuthTokenRequired: boolean = true, params?: any, isEnableLoader: boolean = true, loaderType: LoaderType = 'content'): Observable<Response> {
    return this.doPost(baseUrl + url, data, isAuthTokenRequired, params, isEnableLoader, loaderType);
  }
  put(url: string, data: any, isAuthTokenRequired: boolean = true, httpParams?: any, isEnableLoader: boolean = true, loaderType: LoaderType = 'content'): Observable<Response> {
    return this.doPut(baseUrl + url, data, isAuthTokenRequired, httpParams, isEnableLoader, loaderType);
  }

  postWithId(url: string, data: any, params?: any, isEnableLoader: boolean = true): Observable<Response> {
    return this.doPost(baseUrl + url, data, true, params, isEnableLoader);
  }

  putWithId(url: string, data: any, params?: any, isEnableLoader: boolean = true): Observable<Response> {
    return this.doPut(baseUrl + url, data, true, params, isEnableLoader);
  }

  upload(url: string, data: any, isAuthTokenRequired: boolean = true, params?: any, isEnableLoader: boolean = false): Observable<any> {
    return this.doUpload(baseUrl + url, data, isAuthTokenRequired, params, isEnableLoader);
  }

  showFullPageLoader(): void {
    this.fullPageLoadingCount++;
    this.isFullPageLoading = true;
  }

  hideFullPageLoader(): void {
    this.fullPageLoadingCount = Math.max(0, this.fullPageLoadingCount - 1);
    this.isFullPageLoading = this.fullPageLoadingCount > 0;
  }

  private showLoader(loaderType: LoaderType = 'content'): void {
    if (loaderType === 'full') {
      this.showFullPageLoader();
      return;
    }
    this.contentLoadingCount++;
    this.isLoading = true;
  }

  private hideLoader(loaderType: LoaderType = 'content'): void {
    if (loaderType === 'full') {
      this.hideFullPageLoader();
      return;
    }
    this.contentLoadingCount = Math.max(0, this.contentLoadingCount - 1);
    this.isLoading = this.contentLoadingCount > 0;
  }

  private doUpload(url: string, data: any, isAuthTokenRequired: boolean = true, params?: any, isEnableLoader: boolean = false): Observable<any> {
    let headers = new HttpHeaders();
    headers = this.securityService.setHeadersToUpload(isAuthTokenRequired).headers;

    if (isEnableLoader) {
      this.showLoader('content');
    }

    return this.http.post(url, data, { headers: headers, reportProgress: true, observe: 'events' })
      .pipe(
        tap((res: HttpEvent<Object>) => {
          return res;
        }),
        catchError(error => {
          console.log('the error ', error?.error?.message)
          if (error?.error?.message !== undefined) {
            this.toaster.error(error?.error?.message)
          }
          return throwError(0)
        }),
        finalize(() => {
          if (isEnableLoader) {
            this.hideLoader('content');
          }
        })
      );
  }

  public get(url: string, params?: any, isEnableLoader: boolean = true): Observable<Response> {
    if (isEnableLoader) {
      this.showLoader('content');
    }

    let httpOptions = {
      headers: new HttpHeaders()
    };

    httpOptions = this.securityService.setHeaders(true);

    return this.http.get(baseUrl + url, httpOptions)
      .pipe(
        tap((res: any) => {
          return res;
        }),
        catchError(error => {
          console.log('the error ', error?.error?.message)
          if (error?.error?.message !== undefined) {
            this.toaster.error(error?.error?.message)
          }
          return throwError(0)
        }),
        finalize(() => {
          if (isEnableLoader) {
            this.hideLoader('content');
          }
        })
      );
  }

  private doPost(url: string, data: any, isAuthTokenRequired: boolean = true, params?: any, isEnableLoader: boolean = true, loaderType: LoaderType = 'content'): Observable<Response> {
    let httpOptions = {
      headers: new HttpHeaders()
    };

    if (isEnableLoader) {
      this.showLoader(loaderType);
    }

    httpOptions = this.securityService.setHeaders(isAuthTokenRequired);
    return this.http.post(url, data, httpOptions)
      .pipe(
        tap((res: any) => {
          return res;
        }),
        catchError(error => {
          console.log('the error ', error?.error?.message)
          if (error?.error?.message !== undefined) {
            this.toaster.error(error?.error?.message)
          }
          return throwError(0)
        }),
        finalize(() => {
          if (isEnableLoader) {
            this.hideLoader(loaderType);
          }
        })
      );
  }

  public delete(url: string, params?: any, isEnableLoader: boolean = true) {
    if (isEnableLoader) {
      this.showLoader('content');
    }

    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: {
        id: params,
      },
    };

    return this.http.delete(baseUrl + url, httpOptions).pipe(
      tap((res: any) => {
        console.log(res);
        return res;
      }),
      catchError(error => {
        console.log('the error ', error?.error?.message)
        if (error?.error?.message !== undefined) {
          this.toaster.error(error?.error?.message)
        }
        return throwError(0)
      }),
      finalize(() => {
        if (isEnableLoader) {
          this.hideLoader('content');
        }
      })
    );
  }

  private doPut(url: string, data: any, needId: boolean, params?: any, isEnableLoader: boolean = true, loaderType: LoaderType = 'content'): Observable<Response> {
    let httpOptions = {
      headers: new HttpHeaders()
    };

    if (isEnableLoader) {
      this.showLoader(loaderType);
    }

    httpOptions = this.securityService.setHeaders(true);

    return this.http.put(url, data, httpOptions)
      .pipe(
        tap((res: any) => {
          return res;
        }),
        catchError(error => {
          console.log('the error ', error?.error?.message)
          if (error?.error?.message !== undefined) {
            this.toaster.error(error?.error?.message)
          }
          return throwError(0)
        }),
        finalize(() => {
          if (isEnableLoader) {
            this.hideLoader(loaderType);
          }
        })
      );
  }

  private handleError(error: any) {
    if (error.error instanceof ErrorEvent) {
      console.error('Client side network error occurred:', error.error.message);
    }
    return throwError(error || 'server error');
  }
}
